import {
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  SvgIcon,
  Typography,
} from "@mui/material";

import {
  ArrowDownRight,
  ArrowUpRight,
  BadgeInfoIcon,
  LightbulbIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { DeviationMetric, DeviationText } from "../components/DeviationText";
import { StatCard } from "../components/StatCard";

type SummaryCardProps = {
  deviations: DeviationMetric[] | null;
};

export function SummaryCard({ deviations }: SummaryCardProps) {
  const { t } = useTranslation();

  if (!deviations) {
    return null;
  }

  return (
    <StatCard>
      <SvgIcon component={BadgeInfoIcon} color={"neutral.decoration"} />
      <Typography
        variant={"bodySmallMedium"}
        sx={{
          color: "neutral.subtle",
        }}
      >
        {t("Summary")}
      </Typography>
      {deviations.length === 0 ? (
        <Typography
          variant={"bodySmallRegular"}
          sx={{
            color: "neutral.subtle",
          }}
        >
          {t("Stable night with no registered deviations")}
        </Typography>
      ) : (
        <List dense sx={{ padding: 0 }}>
          {deviations.map((deviation) => (
            <ListItem key={deviation.key} dense sx={{ padding: 0 }}>
              <ListItemIcon sx={{ minWidth: 0 }}>
                <Box
                  sx={(theme) => ({
                    backgroundColor:
                      theme.vars.palette.backgrounds.danger.soft.default,
                    color: theme.vars.palette.texts.danger.decoration,
                    lineHeight: "12px",
                    paddingInline: 1,
                    paddingBlock: 0.5,
                    borderRadius: theme.spacing(1.5),
                  })}
                >
                  {deviation.deviation && deviation.deviation > 0 ? (
                    <ArrowUpRight size={12} />
                  ) : (
                    <ArrowDownRight size={12} />
                  )}
                </Box>
              </ListItemIcon>
              <ListItemText
                slotProps={{
                  primary: {
                    variant: "bodySmallRegular",
                    sx: { paddingLeft: 1 },
                  },
                }}
              >
                <DeviationText metric={deviation} />
              </ListItemText>
            </ListItem>
          ))}
        </List>
      )}
      {deviations.length > 0 && (
        <Chip
          label={t("Consider need for further follow-up")}
          icon={<LightbulbIcon size={16} />}
          sx={(theme) => ({
            color: theme.vars.palette.texts.neutral.default,
            backgroundColor:
              theme.vars.palette.backgrounds.neutral.moderate.default,
          })}
        />
      )}
    </StatCard>
  );
}
